import React, { useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Sparkles, Heart, Briefcase, Activity, Bookmark } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PREDICTIONS, KUNDLI } from "@/constants/testIds";
import { toast } from "sonner";

const SignPicker = ({ value, onChange }) => {
  const [signs, setSigns] = useState([]);
  useEffect(() => { api.get("/meta/zodiac").then((r) => setSigns(r.data)); }, []);
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
      {signs.map((s) => (
        <button
          key={s.key}
          type="button"
          data-testid={PREDICTIONS.signSelector(s.key)}
          onClick={() => onChange(s.key)}
          className={`p-4 border text-center luxe-btn ${value === s.key ? "border-primary bg-primary/5" : "border-border hover:border-secondary"}`}
        >
          <div className="text-2xl text-secondary">{s.symbol}</div>
          <div className="text-xs mt-1 uppercase tracking-label">{s.name}</div>
          <div className="text-[10px] text-muted-foreground mt-0.5">{s.dates}</div>
        </button>
      ))}
    </div>
  );
};

const ResultCard = ({ text, testId }) => (
  <div data-testid={testId} className="editorial-card p-8 mt-8 whitespace-pre-wrap text-[15px] leading-relaxed">
    {text}
  </div>
);

const Predictions = () => {
  const { lang, user } = useApp();
  const [sign, setSign] = useState("aries");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [daily, setDaily] = useState("");
  const [mini, setMini] = useState("");
  const [miniTopic, setMiniTopic] = useState("love");
  const [kundli, setKundli] = useState("");
  const [k, setK] = useState({ name: "", dob: "", tob: "", pob: "" });

  const genDaily = async () => {
    setLoading(true); setDaily("");
    try {
      const r = await api.post("/predictions/daily", { sign, lang });
      setDaily(r.data.prediction);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setLoading(false);
  };

  const genMini = async (topic) => {
    setMiniTopic(topic);
    setLoading(true); setMini("");
    try {
      const r = await api.post("/predictions/mini", { sign, topic, lang });
      setMini(r.data.prediction);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setLoading(false);
  };

  const genKundli = async () => {
    if (!k.name || !k.dob || !k.tob || !k.pob) { toast.error("Please fill all birth details"); return; }
    setLoading(true); setKundli("");
    try {
      const r = await api.post("/predictions/kundli", { ...k, lang });
      setKundli(r.data.prediction);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setLoading(false);
  };

  const saveKundli = async () => {
    if (!user) { toast.info("Sign in to save readings to your account"); return; }
    if (!kundli) return;
    setSaving(true);
    try {
      await api.post("/me/kundlis", { ...k, lang, prediction: kundli });
      toast.success("Reading saved to your account");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    setSaving(false);
  };

  return (
    <div className="max-w-6xl mx-auto px-6 sm:px-10 py-16">
      <p className="text-xs uppercase tracking-label text-secondary">Vedic Astrology</p>
      <h1 className="display-serif text-5xl md:text-6xl mt-3">Your reading, written in the language of your heart.</h1>
      <p className="mt-4 text-muted-foreground max-w-2xl">All predictions are generated by Claude Sonnet 4.5 with authentic Vedic prompts, delivered in your chosen language.</p>

      <Tabs defaultValue="daily" className="mt-12">
        <TabsList className="grid grid-cols-3 max-w-lg bg-muted p-1 rounded-none">
          <TabsTrigger value="daily" data-testid={PREDICTIONS.tabDaily} className="uppercase tracking-label text-xs data-[state=active]:bg-background rounded-none">Daily</TabsTrigger>
          <TabsTrigger value="kundli" data-testid={PREDICTIONS.tabKundli} className="uppercase tracking-label text-xs data-[state=active]:bg-background rounded-none">Kundli</TabsTrigger>
          <TabsTrigger value="mini" data-testid={PREDICTIONS.tabMini} className="uppercase tracking-label text-xs data-[state=active]:bg-background rounded-none">Love · Career · Health</TabsTrigger>
        </TabsList>

        <TabsContent value="daily" className="mt-8">
          <p className="text-sm text-muted-foreground mb-4 uppercase tracking-label">Choose your Rashi</p>
          <SignPicker value={sign} onChange={setSign} />
          <button
            onClick={genDaily}
            disabled={loading}
            data-testid={PREDICTIONS.dailyGenerateBtn}
            className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground text-xs uppercase tracking-label font-bold hover:bg-primary/90 luxe-btn disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" strokeWidth={1.5} />}
            Generate today&apos;s horoscope
          </button>
          {daily && <ResultCard text={daily} testId={PREDICTIONS.dailyResult} />}
        </TabsContent>

        <TabsContent value="kundli" className="mt-8">
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl">
            <div>
              <Label htmlFor="k-name" className="text-xs uppercase tracking-label">Full Name</Label>
              <Input id="k-name" data-testid={PREDICTIONS.kundliName} value={k.name} onChange={(e) => setK({ ...k, name: e.target.value })} className="mt-2 rounded-none border-0 border-b bg-transparent" />
            </div>
            <div>
              <Label htmlFor="k-dob" className="text-xs uppercase tracking-label">Date of Birth</Label>
              <Input id="k-dob" type="date" data-testid={PREDICTIONS.kundliDob} value={k.dob} onChange={(e) => setK({ ...k, dob: e.target.value })} className="mt-2 rounded-none border-0 border-b bg-transparent" />
            </div>
            <div>
              <Label htmlFor="k-tob" className="text-xs uppercase tracking-label">Time of Birth</Label>
              <Input id="k-tob" type="time" data-testid={PREDICTIONS.kundliTob} value={k.tob} onChange={(e) => setK({ ...k, tob: e.target.value })} className="mt-2 rounded-none border-0 border-b bg-transparent" />
            </div>
            <div>
              <Label htmlFor="k-pob" className="text-xs uppercase tracking-label">Place of Birth</Label>
              <Input id="k-pob" data-testid={PREDICTIONS.kundliPob} value={k.pob} onChange={(e) => setK({ ...k, pob: e.target.value })} placeholder="City, State" className="mt-2 rounded-none border-0 border-b bg-transparent" />
            </div>
          </div>
          <button
            onClick={genKundli}
            disabled={loading}
            data-testid={PREDICTIONS.kundliGenerateBtn}
            className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground text-xs uppercase tracking-label font-bold hover:bg-primary/90 luxe-btn disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" strokeWidth={1.5} />}
            Generate my Kundli reading
          </button>
          {kundli && (
            <>
              <ResultCard text={kundli} testId={PREDICTIONS.kundliResult} />
              <button
                onClick={saveKundli}
                disabled={saving}
                data-testid={KUNDLI.saveBtn}
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 border border-foreground text-xs uppercase tracking-label font-bold hover:bg-foreground hover:text-background luxe-btn disabled:opacity-50"
              >
                <Bookmark className="w-4 h-4" strokeWidth={1.5} />
                {saving ? "Saving…" : user ? "Save this reading" : "Sign in to save"}
              </button>
            </>
          )}
        </TabsContent>

        <TabsContent value="mini" className="mt-8">
          <p className="text-sm text-muted-foreground mb-4 uppercase tracking-label">Choose your Rashi</p>
          <SignPicker value={sign} onChange={setSign} />
          <div className="mt-8 flex flex-wrap gap-3">
            {[{k:"love", i: Heart}, {k:"career", i: Briefcase}, {k:"health", i: Activity}].map(({k, i: Icon}) => (
              <button
                key={k}
                data-testid={PREDICTIONS.miniTopic(k)}
                onClick={() => genMini(k)}
                disabled={loading}
                className={`inline-flex items-center gap-2 px-5 py-3 border text-xs uppercase tracking-label font-bold luxe-btn ${miniTopic === k ? "border-primary bg-primary/5" : "border-border hover:border-secondary"}`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.5} /> {k}
              </button>
            ))}
          </div>
          {loading && <div className="mt-6 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Consulting the stars…</div>}
          {mini && <ResultCard text={mini} testId={PREDICTIONS.miniResult} />}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Predictions;
